import { supabase } from '../client'
import type { CloudProject } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── PROJECTS (Cloud) ─────────────────────────────────────────── */

/**
 * Get all projects for user
 */
export async function getCloudProjects(userId: string, since?: Date): Promise<CloudProject[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('order', { ascending: true })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get projects error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get projects error', error)
    return []
  }
}

/**
 * Get all cloud project IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect projects missing from local DB.
 */
export async function getCloudProjectIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('projects')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      logSupabaseError('Get project IDs error', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get project IDs error', error)
    return []
  }
}

/**
 * Get specific cloud projects by their IDs.
 * Used for targeted fetch of projects missing from local DB.
 */
export async function getCloudProjectsByIds(userId: string, ids: string[]): Promise<CloudProject[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      logSupabaseError('Get projects by IDs error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get projects by IDs error', error)
    return []
  }
}

/**
 * Get cloud projects that were deleted since a given date
 */
export async function getDeletedCloudProjects(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('projects')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get deleted projects error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get deleted projects error', error)
    return []
  }
}

/**
 * Create a new project
 */
export async function createCloudProject(
  userId: string,
  project: Omit<CloudProject, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudProject | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('projects')
      .insert({
        ...project,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      logSupabaseError('Create project error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Create project error', error)
    return null
  }
}

/**
 * Update a project
 */
export async function updateCloudProject(
  projectId: string,
  updates: Partial<Omit<CloudProject, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('projects')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', projectId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      logSupabaseError('Update project error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Update project error', error)
    return null
  }
}

/**
 * Delete a project (soft delete)
 */
export async function deleteCloudProject(projectId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('projects')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', projectId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      logSupabaseError('Delete project error', error)
      return false
    }

    return true
  } catch (error) {
    logSupabaseError('Delete project error', error)
    return false
  }
}
